/**
 * PICKER FIRST — where a stranger's very first load lands (job #784).
 *
 * Tom, 2026-09-23: "if this is the case for the very first load for a user
 * with no account and no local cache then we should send them to the course
 * picker tab first; they then complete the I speak; and then they get the list
 * of all the possible langs; so when they select a language, it's already
 * started preparing the first bundle by the time they finish this choice".
 *
 * So a bare `/` from somebody this device has never seen goes to `/start`,
 * the picker, and not to the player. The player chunk is not on that page's
 * critical path; it starts downloading when "I speak" is set, and the course
 * starts downloading when a language is pressed. Everything else is untouched:
 *
 *  - a shared course link names its course, so the choice is already made
 *    and it goes straight to the player (`?course=`, `?script=`, and any
 *    other query the app acts on);
 *  - a signed-in visitor, a returning one, or one with a remembered course is
 *    not a stranger, and lands where they always have.
 *
 * Detection is local storage only: cheap, synchronous, and read once at boot
 * before anything in this session writes to it.
 */
import { ref } from 'vue'
import { LAST_COURSE_KEY } from './courseChoice'

export const PICKER_FIRST_PATH = '/start'

/**
 * Query keys that say nothing about what the visitor wants from the app —
 * campaign tags and referrers. Any OTHER key is an instruction the app acts
 * on (a course, a Script ladder, a screen, a recovery switch, a dev cheat), so
 * the visit is not a bare first load and the picker keeps out of its way.
 */
const HARMLESS_QUERY_KEY = /^(utm_[a-z_]+|fbclid|gclid|msclkid|ref|src|source)$/i

export interface StorageLike {
  getItem(key: string): string | null
  readonly length: number
  key(index: number): string | null
}

export interface FirstVisitSignals {
  /** window.location.search */
  search: string
  local: StorageLike | null
  session: StorageLike | null
}

function hasSignedInSession(local: StorageLike): boolean {
  for (let i = 0; i < local.length; i++) {
    const k = local.key(i)
    if (k && /^sb-.+-auth-token$/.test(k) && local.getItem(k)) return true
  }
  return false
}

/**
 * Is this the very first load of somebody with no account and nothing cached,
 * who has not named a course? True sends them to the picker.
 */
export function isStrangerFirstLoad({ search, local, session }: FirstVisitSignals): boolean {
  try {
    const params = new URLSearchParams(search || '')
    for (const key of params.keys()) if (!HARMLESS_QUERY_KEY.test(key)) return false
    // No storage at all (private mode that throws, a locked-down webview):
    // we cannot tell a stranger from anyone else, so leave the old landing.
    if (!local) return false
    if (hasSignedInSession(local)) return false
    if (local.getItem('ssi-has-played') === 'true') return false
    if (local.getItem(LAST_COURSE_KEY)) return false
    if (session?.getItem('ssi-demo-last-course')) return false
    return true
  } catch {
    return false
  }
}

/**
 * Where the router's FIRST navigation to the bare player should go instead,
 * or null to carry on to the player. Only the first navigation of the
 * document: an in-app tap on Learn always reaches the player.
 */
export function pickerFirstRedirect(opts: {
  isFirstNavigation: boolean
  path: string
  signals: FirstVisitSignals
}): string | null {
  if (!opts.isFirstNavigation || opts.path !== '/') return null
  return isStrangerFirstLoad(opts.signals) ? PICKER_FIRST_PATH : null
}

/** The signals from the real window, or none (tests, SSR). */
export function windowSignals(): FirstVisitSignals {
  const safe = (get: () => StorageLike): StorageLike | null => {
    try { return get() } catch { return null }
  }
  return {
    search: typeof window !== 'undefined' ? window.location.search : '',
    local: typeof window !== 'undefined' ? safe(() => window.localStorage) : null,
    session: typeof window !== 'undefined' ? safe(() => window.sessionStorage) : null,
  }
}

/**
 * True while the stranger is on the picker and has not yet chosen. App.vue
 * reads it to hold back its own fallback course: assigning one would set up a
 * data provider and a content-version query for a course the stranger has not
 * picked and probably never will.
 */
export const pickerFirstActive = ref(false)
